import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { LineChart, Mail } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [mt5Login, setMt5Login] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(email, password, name, mt5Login);
      setPendingVerify(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (pendingVerify) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center p-5">
        <div className="w-full max-w-md border border-white/10 bg-[#121214] rounded-sm p-6 sm:p-8 text-center" data-testid="register-verify-screen">
          <div className="flex items-center justify-center gap-2 mb-6">
            <LineChart className="w-5 h-5 text-teal-400" strokeWidth={1.5} />
            <div className="font-mono text-sm tracking-widest uppercase">BonBot</div>
          </div>
          <Mail className="w-12 h-12 text-teal-400 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-2xl font-medium tracking-tight mb-2">Check your inbox</div>
          <p className="text-zinc-500 text-sm mb-6">
            We sent a verification link to <span className="text-zinc-200">{email}</span>.
            Click it to activate your account and connect a broker.
          </p>
          <Link to="/login" data-testid="register-to-login"
                className="inline-block bg-teal-400 text-[#09090B] hover:bg-teal-300 px-6 py-2.5 text-sm font-mono uppercase tracking-widest rounded-sm font-medium">
            Continue to sign in
          </Link>
          <div className="text-[10px] text-zinc-600 mt-6">
            Didn't receive it? Check spam, or sign in and click "Resend verification".
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex">
      <div className="w-full md:w-[480px] flex flex-col px-6 sm:px-10 py-12 border-r border-white/10 bg-[#09090B]/80 backdrop-blur-sm relative">
        <div className="flex items-center gap-2 mb-10">
          <LineChart className="w-5 h-5 text-teal-400" strokeWidth={1.5} />
          <div className="font-mono text-sm tracking-widest uppercase">BonBot</div>
        </div>
        <div className="my-auto">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">New Account</div>
          <h1 className="text-3xl font-medium tracking-tight mb-2">Create your trader account.</h1>
          <p className="text-zinc-500 text-sm mb-8">We'll send a verification email to activate your access.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Full Name</label>
              <input
                data-testid="register-name-input"
                value={name} onChange={(e) => setName(e.target.value)}
                className="w-full mt-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                placeholder="Jane Trader"
                required
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Email</label>
              <input
                data-testid="register-email-input"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                placeholder="trader@example.com"
                required
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">MT5 Login Number (default account)</label>
              <input
                data-testid="register-mt5-login-input"
                value={mt5Login} onChange={(e) => setMt5Login(e.target.value)}
                className="w-full mt-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                placeholder="e.g. 105712107"
                inputMode="numeric"
                required
              />
              <div className="text-[10px] text-zinc-600 mt-1">Pre-links this MT5 account to your profile. You'll set the broker / server / password after signing in.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Password</label>
              <input
                data-testid="register-password-input"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none"
                placeholder="At least 6 characters"
                minLength={6}
                required
              />
            </div>
            {err && <div className="text-rose-400 text-sm font-mono" data-testid="register-error">{err}</div>}
            <button
              data-testid="register-submit-button"
              type="submit" disabled={loading}
              className="w-full bg-zinc-100 text-zinc-900 hover:bg-white py-2.5 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
            >
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            Already a trader?{" "}
            <Link to="/login" className="text-teal-400 hover:text-teal-300" data-testid="link-login">
              Sign in
            </Link>
          </div>
        </div>
      </div>
      <div className="hidden md:flex flex-1 items-center justify-center" />
    </div>
  );
}
